import React, { useState, useEffect, useMemo } from "react";
import { FaLock, FaCrown, FaTag, FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";

import { functions } from "../../../firebase";
import useFemaleProfile from "../../../hooks/useFemaleProfile";

import TopBar from "../../common/TopBar";
import "./ChatPricePage.css";
import goldCoin from "../../../assets/gold.png";
import tokenImg from "../../../assets/token.png";

// =======================================================
// 💰 أسعار الاتصال الرسمية من السيرفر
// =======================================================
const FEMALE_CALL_PRICES = {
  0: 20,
  1: 20,
  2: 25,
  3: 30,
  4: 35,
  5: 40,
  6: 45,
  7: 50,
  8: 60,
  9: 70,
  10: 80,
};

// =======================================================
// 👩 نسب أرباح الفتاة الرسمية
// =======================================================
const FEMALE_SHARES = {
  0: 0.40,
  1: 0.40,
  2: 0.42,
  3: 0.44,
  4: 0.46,
  5: 0.48,
  6: 0.48,
  7: 0.48,
  8: 0.48,
  9: 0.50,
  10: 0.50,
};

export default function ChatPricePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { profile, loading } = useFemaleProfile();

  const [selectedLevel, setSelectedLevel] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;

    const realLevel = Number(profile?.femaleLevel ?? 0);
    const rawCallPriceLevel = Number(profile?.callPriceLevel ?? realLevel);
    const safeLevel = Math.min(rawCallPriceLevel, realLevel);

    setSelectedLevel(safeLevel);
  }, [profile]);

  const updateCallPriceLevel = useMemo(
    () => httpsCallable(functions, "updateCallPriceLevel"),
    []
  );

  const saveLevel = async (lvl) => {
    try {
      setSaving(true);

      await updateCallPriceLevel({
        callPriceLevel: Number(lvl),
      });

      setSelectedLevel(Number(lvl));
    } catch (e) {
      alert(
        e?.message ||
          t("pricing.chat.saveFailed", { defaultValue: "Save failed" })
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pageLoading">
        {t("common.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }

  if (!profile) return null;

  const realLevel = Number(profile?.femaleLevel ?? 0);
  const currentPrice = FEMALE_CALL_PRICES[selectedLevel] ?? 0;
  const currentShare = FEMALE_SHARES[selectedLevel] ?? 0.4;
  const currentFemaleEarning = Math.round(currentPrice * currentShare);

  return (
    <div className="chatPricePage">
      <TopBar
        title={t("pricing.chat.title", {
          defaultValue: "Chat price settings",
        })}
        onBack={() => navigate(-1)}
      />

      <div className="chatPricePageBody">
        <h2 className="chatPriceSummaryTitle">
          {t("pricing.chat.videoLabel", {
            defaultValue: "سعر دردشة الفيديو",
          })}
        </h2>

        <div className="chatPriceSummaryCard">
          <div className="chatPriceMainBox">
            <div className="chatSummaryCol">
              <span className="chatSummaryLabel">
                {t("pricing.chat.realLevel", {
                  defaultValue: "مستواك الحقيقي",
                })}
              </span>
              <div className="chatSummaryBadge real">
                <FaCrown className="chatSummaryBadgeIcon" aria-hidden />
                <span>
                  {t("pricing.chat.levelLabel", {
                    defaultValue: "Lv{{level}}",
                    level: realLevel,
                  })}
                </span>
              </div>
            </div>

            <div className="chatSummaryCol chatSummaryCol--price">
              <span className="chatSummaryLabel">
                {t("pricing.chat.callPrice", {
                  defaultValue: "السعر الحالي",
                })}
              </span>
              <div className="summaryPriceValue">
                <strong>{currentPrice}</strong>
                <img
                  src={goldCoin}
                  alt={t("pricing.chat.maleCoinAlt", { defaultValue: "Coin" })}
                  className="inlineCoin summaryCoin"
                />
              </div>
              <div className="summaryPriceUnit">
                {t("pricing.chat.perMinute", { defaultValue: "/ دقيقة" })}
              </div>
            </div>

            <div className="chatSummaryCol">
              <span className="chatSummaryLabel">
                {t("pricing.chat.currentLevel", {
                  defaultValue: "مستوى التسعير",
                })}
              </span>
              <div className="chatSummaryBadge pricing">
                <FaTag className="chatSummaryBadgeIcon" aria-hidden />
                <span>
                  {t("pricing.chat.levelLabel", {
                    defaultValue: "Lv{{level}}",
                    level: selectedLevel,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="chatPriceEarningsLine">
          <img
            src={tokenImg}
            alt={t("pricing.chat.femaleCoinAlt", {
              defaultValue: "Female coin",
            })}
            className="inlineCoin small"
          />
          <span>
            {t("pricing.chat.femaleEarn", {
              defaultValue: "أرباحك من الدردشة",
            })}
            : <strong>{currentFemaleEarning}</strong>
          </span>
        </div>

        <div className="priceCardsWrap">
          {Object.keys(FEMALE_CALL_PRICES)
            .map(Number)
            .map((lvl) => {
              const isUnlocked = lvl <= realLevel;
              const isActive = lvl === selectedLevel;
              const price = FEMALE_CALL_PRICES[lvl];
              const share = FEMALE_SHARES[lvl] ?? 0.4;
              const femaleEarning = Math.round(price * share);
              const sharePercent = Math.round(share * 100);

              return (
                <div
                  key={lvl}
                  className={`priceLevelCard ${
                    isActive ? "active" : ""
                  } ${!isUnlocked ? "locked" : ""}`}
                >
                  <div className="priceLevelTop">
                    <div className="priceLevelBadge">
                      {t("pricing.chat.levelLabel", {
                        defaultValue: "Lv{{level}}",
                        level: lvl,
                      })}
                    </div>

                    {!isUnlocked && (
                      <div className="priceLevelLock">
                        <FaLock />
                      </div>
                    )}
                  </div>

                  <div className="priceBlock">
                    <span className="miniLabel">
                      {t("pricing.chat.callPrice", {
                        defaultValue: "سعر المكالمة",
                      })}
                    </span>

                    <div className="priceLevelPrice">
                      <img
                        src={goldCoin}
                        alt={t("pricing.chat.maleCoinAlt", {
                          defaultValue: "Coin",
                        })}
                        className="inlineCoin"
                      />
                      <strong>{price}</strong>
                      <span className="unit">
                        {t("pricing.chat.perMinute", {
                          defaultValue: "/ دقيقة",
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="earnRow">
                    <div className="earnItem">
                      <span className="miniLabel">
                        {t("pricing.chat.femaleShare", {
                          defaultValue: "حصة الأنثى",
                        })}
                      </span>
                      <div className="earnPercent">{sharePercent}%</div>
                    </div>

                    <div className="earnDivider" />

                    <div className="earnItem">
                      <span className="miniLabel">
                        {t("pricing.chat.femaleEarn", {
                          defaultValue: "أرباحك الآن",
                        })}
                      </span>

                      <div className="earnValue">
                        <img
                          src={tokenImg}
                          alt={t("pricing.chat.femaleCoinAlt", {
                            defaultValue: "Female coin",
                          })}
                          className="inlineCoin small"
                        />
                        <strong>{femaleEarning}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="priceLevelStatus">
                    {isUnlocked
                      ? t("pricing.chat.available", {
                          defaultValue: "متاح",
                        })
                      : t("pricing.chat.locked", {
                          defaultValue: "غير متاح بعد",
                        })}
                  </div>

                  <button
                    type="button"
                    className={`priceSelectBtn ${isActive ? "active" : ""}`}
                    disabled={!isUnlocked || saving || isActive}
                    onClick={() => saveLevel(lvl)}
                  >
                    {isActive ? (
                      <>
                        <FaCheck className="priceSelectBtnIcon" aria-hidden />
                        {t("pricing.chat.selected", {
                          defaultValue: "محدد",
                        })}
                      </>
                    ) : (
                      t("pricing.chat.select", {
                        defaultValue: "اختيار",
                      })
                    )}
                  </button>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
